import { AcceptUiButton } from '@/components/AcceptUiButton';
import { api } from '@/lib/api';
import type { AcceptUiPaymentResult } from '@/types/acceptUi';
import { CheckoutRequest, PaymentConfig } from '@/types';
import {
  Box,
  Group,
  LoadingOverlay,
  Paper,
  Select,
  Stack,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCreditCard } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// US State codes for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

interface FormValues {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

export interface PaymentFormProps {
  amount: number;
  onSubmit: (data: CheckoutRequest) => Promise<void>;
  disabled?: boolean;
  resetTrigger?: number;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ amount, onSubmit, disabled = false, resetTrigger = 0 }) => {
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  useEffect(() => {
    submittedRef.current = false;
  }, [resetTrigger]);

  const form = useForm<FormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
    },
    validate: {
      firstName: (v) => (!v.trim() ? 'First name is required' : null),
      lastName: (v) => (!v.trim() ? 'Last name is required' : null),
      address: (v) => (!v.trim() ? 'Address is required' : null),
      city: (v) => (!v.trim() ? 'City is required' : null),
      state: (v) => (!v ? 'State is required' : null),
      zip: (v) => {
        if (!v.trim()) return 'ZIP is required';
        if (!/^\d{5}(-\d{4})?$/.test(v)) return 'Invalid ZIP code';
        return null;
      },
    },
  });

  // Fetch payment config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const config: PaymentConfig = await api.get('/config/payment');
        setPaymentConfig(config);
      } catch (error) {
        console.error('Failed to load payment configuration:', error);
        notifications.show({
          title: 'Configuration Error',
          message: 'Unable to load payment configuration. Please refresh the page.',
          color: 'red',
          position: 'top-center',
          'aria-live': 'polite',
          closeButtonProps: {
            'aria-label': 'Close error notification',
          },
        });
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleAcceptUiPayment = useCallback(
    async (result: AcceptUiPaymentResult) => {
      const validation = form.validate();
      if (validation.hasErrors) return;
      if (submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);
      try {
        await onSubmit({
          paymentNonce: result.nonce,
          billingInfo: { ...form.values },
          cardLast4: result.cardLast4,
          cardType: result.cardType,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [form, onSubmit]
  );

  const buttonDisabled = disabled || submitting || !paymentConfig || !form.isValid();

  return (
    <Box pos="relative">
      <LoadingOverlay
        aria-live="polite"
        aria-label="Loading"
        visible={configLoading}
        overlayProps={{ blur: 2, radius: 'sm' }}
        loaderProps={{ size: 'xl' }}
      />

      <Stack gap="md">
        <Paper p="md" withBorder>
          <Stack gap="md">
            <Group gap="xs">
              <IconCreditCard size={20} color="var(--mantine-color-pq-red-6)" aria-hidden />
              <Title order={3}>Billing Information</Title>
            </Group>
            <Group grow>
              <TextInput
                label="First Name"
                required
                {...form.getInputProps('firstName')}
              />
              <TextInput
                label="Last Name"
                required
                {...form.getInputProps('lastName')}
              />
            </Group>
            <TextInput
              label="Address"
              required
              {...form.getInputProps('address')}
            />
            <Group grow>
              <TextInput
                label="City"
                required
                {...form.getInputProps('city')}
              />
              <Select
                label="State"
                required
                searchable
                data={US_STATES}
                {...form.getInputProps('state')}
              />
              <TextInput
                label="ZIP"
                required
                {...form.getInputProps('zip')}
              />
            </Group>
          </Stack>
        </Paper>

        {paymentConfig && (
          <Group justify="center">
            <AcceptUiButton
              key={resetTrigger}
              apiLoginId={paymentConfig.apiLoginId}
              publicClientKey={paymentConfig.publicClientKey}
              environment={paymentConfig.environment}
              onPayment={handleAcceptUiPayment}
              disabled={buttonDisabled}
              billingAddressOptions={{ show: false, required: false }}
              formButtonText={`Pay $${amount.toFixed(2)}`}
              formHeaderText="Card Information"
              size="lg"
              color="pq-red"
              fullWidth
            >
              Pay with Card
            </AcceptUiButton>
          </Group>
        )}
      </Stack>
    </Box>
  );
};

export default PaymentForm;
